/**
 * useBlock Hook
 * Manages block status and actions with a specific user.
 * Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { BlockStatusResponse } from '../blockTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseBlockOptions {
  httpClient: HttpClient;
  targetUserId: string;
  isAuthenticated: boolean;
}

export interface UseBlockResult {
  isBlocked: boolean | null;
  blockedByMe: boolean;
  blockedMe: boolean;
  loading: boolean;
  error: string | null;
  blockUser: () => Promise<void>;
  unblockUser: () => Promise<void>;
  checkBlockStatus: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useBlock(options: UseBlockOptions): UseBlockResult {
  const { httpClient, targetUserId, isAuthenticated } = options;

  const [isBlocked, setIsBlocked] = useState<boolean | null>(null);
  const [blockedByMe, setBlockedByMe] = useState(false);
  const [blockedMe, setBlockedMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkBlockStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setIsBlocked(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<BlockStatusResponse>(`/api/blocks/status/${targetUserId}`);
      setIsBlocked(data.isBlocked);
      setBlockedByMe(data.blockedByMe);
      setBlockedMe(data.blockedMe);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check block status');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  const blockUser = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      await httpClient.post(`/api/blocks/${targetUserId}`);
      setIsBlocked(true);
      setBlockedByMe(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block user');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  const unblockUser = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      await httpClient.delete(`/api/blocks/${targetUserId}`);
      setBlockedByMe(false);
      // blockedMe stays the same — unblocking doesn't affect the other direction
      // Use functional setter to read current blockedMe without adding it to deps
      setBlockedMe((currentBlockedMe) => {
        setIsBlocked(currentBlockedMe);
        return currentBlockedMe;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unblock user');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  return {
    isBlocked,
    blockedByMe,
    blockedMe,
    loading,
    error,
    blockUser,
    unblockUser,
    checkBlockStatus,
  };
}
